// Report viewer/editor - sections are editable text (the report is a document,
// not a locked PDF); metrics panel shows the fact-based lens; finalize triggers
// the print/PDF flow.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent,
  Chip, Divider, Grid, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrintIcon from '@mui/icons-material/Print';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import { useParams } from 'react-router-dom';
import lpsService from '../services/lpsService';
import SessionAnalytics from '../components/SessionAnalytics';

export default function ReportPage() {
  const { recordingId } = useParams();
  const [report, setReport] = useState(null);
  const [recording, setRecording] = useState(null);
  const [sections, setSections] = useState({});
  const [toast, setToast] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBusy, setAudioBusy] = useState(false);

  const playAudio = async () => {
    if (audioUrl) { setAudioUrl(null); return; }
    setAudioBusy(true);
    try {
      setAudioUrl(await lpsService.fetchAudio(recordingId));
    } catch {
      setToast('No audio available for this recording');
    } finally {
      setAudioBusy(false);
    }
  };

  useEffect(() => {
    lpsService.getReport(recordingId).then((r) => { setReport(r); setSections(r.sections || {}); });
    lpsService.getRecording(recordingId).then(setRecording).catch(() => {});
  }, [recordingId]);

  const dirty = useMemo(
    () => report && JSON.stringify(sections) !== JSON.stringify(report.sections),
    [sections, report]);

  const save = async () => {
    await lpsService.saveSections(recordingId, sections);
    setReport({ ...report, sections });
    setToast('Draft saved');
  };

  const finalize = async () => {
    if (dirty) await lpsService.saveSections(recordingId, sections);
    await lpsService.finalize(recordingId);
    setToast('Report finalized');
    setTimeout(() => window.print(), 400);
  };

  if (!report) return <Typography sx={{ p: 4 }}>Loading report…</Typography>;
  const m = report.metrics || {};

  return (
    <Box sx={{ p: 3 }} className="lps-report">
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{recording?.title || 'Report'}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <Chip size="small" label={report.branding?.vertical || report.vertical} />
            <Chip size="small" label={report.status === 'final' ? 'Final' : 'Draft'}
              color={report.status === 'final' ? 'success' : 'warning'} />
            {recording?.subject_id && <Chip size="small" label={`ID ${recording.subject_id}`} />}
          </Stack>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1} className="no-print">
          {recording?.has_audio && (
            <Button startIcon={<PlayArrowIcon />} disabled={audioBusy} onClick={playAudio}>
              {audioUrl ? 'Hide player' : audioBusy ? 'Loading…' : 'Play recording'}
            </Button>
          )}
          <Button startIcon={<SaveIcon />} disabled={!dirty} onClick={save}>Save draft</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={finalize}>
            Finalize & PDF
          </Button>
        </Stack>
      </Stack>

      {audioUrl && (
        <Box className="no-print" sx={{ mb: 2 }}>
          <audio controls autoPlay src={audioUrl} style={{ width: '100%' }} />
        </Box>
      )}

      {report?.analytics && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Session analytics</Typography>
          <SessionAnalytics
            analytics={report.analytics}
            lang={(report.analytics.locale || '').startsWith('he') ? 'he' : 'en'}
          />
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {Object.entries(sections).map(([heading, body]) => (
            <Card key={heading} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="overline" color="primary" fontWeight={700}>{heading}</Typography>
                <TextField
                  fullWidth multiline minRows={2} variant="standard"
                  InputProps={{ disableUnderline: true }}
                  value={body}
                  onChange={(e) => setSections({ ...sections, [heading]: e.target.value })}
                />
              </CardContent>
            </Card>
          ))}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="overline" color="primary" fontWeight={700}>
                Conversation metrics
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <Row k="Duration" v={fmt(m.duration_seconds)} />
                <Row k="Total words" v={m.total_words} />
                <Row k="Silences (≥3s)" v={`${m.silences || 0} · ${m.silence_seconds || 0}s total`} />
                <Row k="Longest silence" v={`${m.longest_silence_seconds || 0}s`} />
                <Divider sx={{ my: 1 }} />
                {(m.speakers || []).map((s) => (
                  <Row key={s.speaker} k={s.speaker}
                    v={`${s.words} words · ${fmt(s.talk_seconds)} · ${s.turns} turns`} />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Fact-based metrics. Emotional analysis is available as an opt-in
                per user and is off by default.
              </Typography>
            </CardContent>
          </Card>

          <Accordion variant="outlined" className="no-print">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="overline" color="primary" fontWeight={700}>Transcript</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ maxHeight: 380, overflow: 'auto' }}>
              {(report.transcript || []).map((s, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                  <b>[{Math.floor(s.start / 60)}:{String(Math.floor(s.start % 60)).padStart(2, '0')}] {s.speaker}:</b> {s.text}
                </Typography>
              ))}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        {report.branding?.footer || ''}
      </Typography>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')} message={toast} />
      <style>{`@media print { .no-print, nav, header { display: none !important } }`}</style>
    </Box>
  );
}

const fmt = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}` : '—');

function Row({ k, v }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">{k}</Typography>
      <Typography variant="body2" fontWeight={600}>{v ?? '—'}</Typography>
    </Stack>
  );
}
